import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Accordion, Form, Dropdown, Modal } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import DatePickerComponent from 'pages/apps/platform/list_node_instances/DatePicker';
import ResetButton from 'components/forms/ResetButton';
import ColumnSelectorRefactor from './ColumnSelectorRefactor';
import Button from 'components/base/Button';

import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { fetchFilters, createFilter, updateFilter, deleteFilter } from '../../redux/slices/filtersSlice';

import { getZones } from '../../redux/slices/lists/zonesSlice';

interface FilterConfig {
  key: string;
  label: string;
  type: 'search' | 'numeric-search' | 'date' | 'dropdown' | 'multi-dropdown' | 'checkbox';
  options?: string[];
  default?: any;
}

interface FilterAndSearchProps {
  filtersConfig: readonly FilterConfig[];
  filters: Record<string, any>;
  pageName: string;
  pageSize: number;
  onFilterChange: (key: string, value: any) => void;
  onReset: () => void;
  onPageSizeChange: (size: number) => void;
  columns: { key: string; label: string }[];
  zoneName?: string | null;
}

const SAVED_FILTER_QUERY_PARAM = 'saved_filter';

const FilterAndSearchRefactor: FC<FilterAndSearchProps> = ({
  filtersConfig,
  filters,
  pageName,
  pageSize,
  onFilterChange,
  onReset,
  onPageSizeChange,
  columns,
  zoneName = null,
}) => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { items: savedFilters, status: filtersStatus } = useSelector((s: any) => {
    return s.filters || { items: [], status: 'idle' };
  });

  // Zones from store (supports common shapes; adjust if your slice differs)
  const zones: any[] = useSelector((s: any) => {
    return s.listOfZones?.zones || [];
  });

  useEffect(() => {
    setSaveZone(zones[0]?.name);
  }, [zones]);

  const [selectedKey, setSelectedKey] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get(SAVED_FILTER_QUERY_PARAM);
  });
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newFilterName, setNewFilterName] = useState('');
  const [newFilterDescription, setNewFilterDescription] = useState('');
  const [renameValue, setRenameValue] = useState('');
  const [saveZone, setSaveZone] = useState<string | null>(zoneName ?? null);

  // Pull only filters for this pageName (filter_target)
  const pageFilters = useMemo(() => (savedFilters || []).filter((f: any) => f.filter_target === pageName), [savedFilters, pageName]);

  const selectedFilter = useMemo(
    () => pageFilters.find((f: any) => `${f.zone_name ?? 'null'}::${f.filter_name}` === selectedKey) || null,
    [pageFilters, selectedKey]
  );

  useEffect(() => {
    // fetch on mount and when pageName changes
    dispatch(fetchFilters({ filter_target: pageName }));
  }, [dispatch, pageName]);

  useEffect(() => {
    // load zones for the Zone selector in the save modal
    dispatch(getZones());
  }, [dispatch]);

  useEffect(() => {
    // keep saveZone in sync with prop changes
    setSaveZone(zoneName ?? null);
  }, [zoneName]);

  const applyFilterData = useCallback(
    (data: Record<string, any>) => {
      if (!data) return;
      Object.entries(data).forEach(([k, v]) => onFilterChange(k, v));
    },
    [onFilterChange]
  );

  useEffect(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : searchParams.toString());
    const currentKey = params.get(SAVED_FILTER_QUERY_PARAM);

    if (selectedKey) {
      if (currentKey === selectedKey) return;
      params.set(SAVED_FILTER_QUERY_PARAM, selectedKey);
      setSearchParams(params, { replace: true });
      return;
    }
    if (!currentKey) return;
    params.delete(SAVED_FILTER_QUERY_PARAM);
    setSearchParams(params, { replace: true });
  }, [selectedKey, searchParams, setSearchParams]);

  const handlePick = (f: any) => {
    const key = `${f.zone_name ?? 'null'}::${f.filter_name}`;
    applyFilterData(f.filter_data || {});

    if (selectedKey === key) {
      return;
    }
    setSelectedKey(key);
  };

  const handleClearPick = () => {
    setSelectedKey(null);
  };

  const handleResetAll = () => {
    setSelectedKey(null);
    onReset();
  };

  const handleSaveNew = async () => {
    if (!newFilterName.trim()) return;
    const trimmedName = newFilterName.trim();
    await dispatch(
      createFilter({
        zone_name: saveZone,
        filter_name: trimmedName,
        filter_target: pageName,
        filter_data: filters,
        description: newFilterDescription.trim() || null,
      })
    );
    setShowSaveModal(false);
    setNewFilterName('');
    setNewFilterDescription('');
    // Select the newly created filter so Update/Delete buttons appear
    const newKey = `${saveZone ?? 'null'}::${trimmedName}`;
    setSelectedKey(newKey);
    dispatch(fetchFilters({ filter_target: pageName }));
  };

  const handleUpdateExisting = async () => {
    if (!selectedFilter) return;
    await dispatch(
      updateFilter({
        zone_name: selectedFilter.zone_name ?? null,
        filter_name: selectedFilter.filter_name,
        data: { filter_data: filters },
      })
    );
    dispatch(fetchFilters({ filter_target: pageName }));
  };

  const handleDeleteExisting = async () => {
    if (!selectedFilter) return;
    const ok = window.confirm(`Delete filter "${selectedFilter.filter_name}"? This cannot be undone.`);
    if (!ok) return;
    await dispatch(
      deleteFilter({
        zone_name: selectedFilter.zone_name ?? null,
        filter_name: selectedFilter.filter_name,
      })
    );
    setSelectedKey(null);
    onReset();
    dispatch(fetchFilters({ filter_target: pageName }));
  };

  const handleOpenRename = () => {
    if (!selectedFilter) return;
    setRenameValue(selectedFilter.filter_name);
    setShowRenameModal(true);
  };

  const handleRenameFilter = async () => {
    if (!selectedFilter || !renameValue.trim()) return;
    const trimmedName = renameValue.trim();
    if (trimmedName === selectedFilter.filter_name) {
      setShowRenameModal(false);
      return;
    }
    await dispatch(
      updateFilter({
        zone_name: selectedFilter.zone_name ?? null,
        filter_name: selectedFilter.filter_name,
        data: { new_filter_name: trimmedName },
      })
    );

    const newKey = `${selectedFilter.zone_name ?? 'null'}::${trimmedName}`;
    setSelectedKey(newKey);
    setShowRenameModal(false);
    setRenameValue('');
    dispatch(fetchFilters({ filter_target: pageName }));
  };

  const renderField = ({ key, label, type, options }: FilterConfig) => {
    const value = filters[key];

    switch (type) {
      case 'search':
        return (
          <Form.Group key={key} className="mb-2">
            <Form.Label className="p-0">{label}</Form.Label>
            <Form.Control type="text" placeholder={`Search ${label}`} size="sm" value={value || ''} onChange={(e) => onFilterChange(key, e.target.value)} />
          </Form.Group>
        );

      case 'date':
        return (
          <Form.Group key={key} className="mb-2">
            <Form.Label className="p-0">{label}</Form.Label>
            <DatePickerComponent
              name={key}
              initialStartDate={value?.startDate}
              initialEndDate={value?.endDate}
              onChange={(_name, startISO, endISO) => {
                if (!startISO && !endISO) {
                  onFilterChange(key, null);
                  return;
                }
                onFilterChange(key, { startDate: startISO, endDate: endISO });
              }}
            />
          </Form.Group>
        );

      case 'dropdown':
        return (
          <Form.Group key={key} className="mb-2">
            <Form.Label className="p-0">{label}</Form.Label>
            <Form.Select value={value || ''} onChange={(e) => onFilterChange(key, e.target.value)}>
              <option value="">All</option>
              {options?.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        );
      case 'multi-dropdown':
        return (
          <Form.Group key={key} className="mb-2">
            <Form.Label className="p-0">{label}</Form.Label>
            <Dropdown autoClose="outside">
              <Dropdown.Toggle variant="outline-secondary">{Array.isArray(value) && value.length ? `${value.length} selected` : 'All'}</Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => onFilterChange(key, [])}>All</Dropdown.Item>
                {options?.map((opt) => {
                  const arr = Array.isArray(value) ? value : [];
                  const isSelected = arr.includes(opt);
                  return (
                    <Dropdown.Item
                      key={opt}
                      onClick={() => {
                        const selected = isSelected ? arr.filter((v: string) => v !== opt) : [...arr, opt];
                        onFilterChange(key, selected);
                      }}
                    >
                      {isSelected ? '✓ ' : ''} {opt}
                    </Dropdown.Item>
                  );
                })}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>
        );
      case 'checkbox':
        return (
          <Form.Group key={key} className="mb-2">
            <Form.Check type="checkbox" label={label} checked={!!value} onChange={(e) => onFilterChange(key, e.target.checked)} />
          </Form.Group>
        );
    }
  };

  return (
    <>
      <Accordion>
        <Accordion.Item eventKey="0">
          <Accordion.Header>Page Settings</Accordion.Header>
          <Accordion.Body className="d-flex gap-3 flex-wrap align-items-end">
            <ResetButton resetPage={pageName} handleReset={handleResetAll} />
            <Form.Group className="mb-2">
              <Form.Label className="p-0">Page Limit</Form.Label>
              <Form.Select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
                {[1, 10, 25, 50, 100].map((limit) => (
                  <option key={limit} value={limit}>
                    {limit}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <Accordion defaultActiveKey="1">
        <Accordion.Item eventKey="1">
          <Accordion.Header>Filters</Accordion.Header>
          <Accordion.Body>
            <Form.Group className="mb-2">
              <Form.Label className="p-0">Saved Filters</Form.Label>
              <Dropdown>
                <Dropdown.Toggle variant="outline-secondary" disabled={filtersStatus === 'loading'}>
                  {selectedFilter ? selectedFilter.filter_name : 'Pick saved filter'}
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ maxHeight: 280, overflowY: 'auto' }}>
                  {pageFilters.length === 0 && <Dropdown.Header className="text-muted">No saved filters</Dropdown.Header>}
                  {pageFilters.map((f: any) => (
                    <Dropdown.Item key={`${f.zone_name ?? 'null'}::${f.filter_name}`} onClick={() => handlePick(f)} title={f.description || ''}>
                      {f.filter_name}
                      {f.description && <small className="text-muted d-block">{f.description}</small>}
                    </Dropdown.Item>
                  ))}
                  {selectedFilter && (
                    <>
                      <Dropdown.Divider />
                      <Dropdown.Item onClick={handleClearPick}>Clear selection</Dropdown.Item>
                    </>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </Form.Group>

            <br />
            {filtersConfig.map(renderField)}

            {!selectedFilter ? (
              <Button variant="primary" onClick={() => setShowSaveModal(true)}>
                Save current as…
              </Button>
            ) : (
              <div className="d-flex gap-2 flex-wrap">
                <Button variant="success" onClick={handleUpdateExisting}>
                  Update
                </Button>
                <Button variant="secondary" onClick={handleOpenRename}>
                  Rename
                </Button>
                <Button variant="danger" onClick={handleDeleteExisting}>
                  Delete
                </Button>
              </div>
            )}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <ColumnSelectorRefactor columns={columns} pageName={pageName} />

      {/* Save modal */}
      <Modal show={showSaveModal} onHide={() => setShowSaveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Save Filter</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Filter name</Form.Label>
            <Form.Control type="text" placeholder="e.g., My favorite view" value={newFilterName} onChange={(e) => setNewFilterName(e.target.value)} />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Description (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Brief description of this filter"
              value={newFilterDescription}
              onChange={(e) => setNewFilterDescription(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Zone</Form.Label>
            <Form.Select
              value={saveZone ?? 'null'}
              onChange={(e) => {
                console.log('saveZone:', saveZone);
                console.log('e.target.value:', e.target.value);
                setSaveZone(e.target.value === 'null' ? null : e.target.value);
              }}
            >
              <option value="null">Global (no zone)</option>
              {zones.map((z: any) => {
                const name = z.zone_name ?? z.name ?? z;
                return (
                  <option key={name} value={name}>
                    {name}
                  </option>
                );
              })}
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowSaveModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveNew} disabled={!newFilterName.trim()}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Rename modal */}
      <Modal show={showRenameModal} onHide={() => setShowRenameModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Rename Filter</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>New filter name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter new name"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && renameValue.trim()) {
                  handleRenameFilter();
                }
              }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowRenameModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleRenameFilter} disabled={!renameValue.trim()}>
            Rename
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FilterAndSearchRefactor;
